import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, paymentAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Dashboard.css';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const MEMBER_PAGE_SIZE = 25;
const TRANSACTION_PAGE_SIZE = 40;
const ADMIN_PAYMENT_PAGE_SIZE = 25;
const getPaymentStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['success', 'partial_success', 'completed', 'paid'].includes(normalized)) return 'online';
  if (['failed', 'rejected', 'cancelled', 'canceled'].includes(normalized)) return 'blocked';
  if (['processing', 'approved'].includes(normalized)) return 'offline';
  return 'pending';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserTransactions, setSelectedUserTransactions] = useState([]);
  const [userTxCategory, setUserTxCategory] = useState('all');
  const [userTxSearch, setUserTxSearch] = useState('');
  const [userTxPage, setUserTxPage] = useState(1);
  const [userTxData, setUserTxData] = useState({
    transactions: [],
    total: 0,
    totalPages: 1,
    categories: [],
    categorySummary: []
  });
  const [userTxLoading, setUserTxLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [balanceTargetUserId, setBalanceTargetUserId] = useState('');
  const [balanceDelta, setBalanceDelta] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceMode, setBalanceMode] = useState('add');
  const [settings, setSettings] = useState({
    chargePercent: 7,
    minimumWager: 50,
    minimumDeposit: 2,
    minimumWithdrawal: 200,
    onlineMembersMode: 'manual',
    onlineMembers: 2847,
    onlineMembersRandomMin: 20,
    onlineMembersRandomMax: 200,
    onlineMembersRandomIntervalSeconds: 30,
    signupCredit: 20,
    referralBonusPercent: 10,
    prizeDistribution: {
      2: [95, 5],
      3: [65, 25, 10],
      4: [60, 25, 10, 5],
      5: [60, 25, 10, 5, 0]
    }
  });
  const [reconcileMinutes, setReconcileMinutes] = useState(180);
  const [reconcileLimit, setReconcileLimit] = useState(200);
  const [reconcileGame, setReconcileGame] = useState('all');
  const [reconcileSummary, setReconcileSummary] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [pendingWithdrawalsLoading, setPendingWithdrawalsLoading] = useState(false);
  const [withdrawActionId, setWithdrawActionId] = useState(null);
  const [adminDeposits, setAdminDeposits] = useState([]);
  const [adminWithdrawals, setAdminWithdrawals] = useState([]);
  const [paymentsViewTab, setPaymentsViewTab] = useState('deposits');
  const [adminDepositsPage, setAdminDepositsPage] = useState(1);
  const [adminDepositsHasMore, setAdminDepositsHasMore] = useState(false);
  const [adminWithdrawalsPage, setAdminWithdrawalsPage] = useState(1);
  const [adminWithdrawalsHasMore, setAdminWithdrawalsHasMore] = useState(false);
  const [adminPaymentsLoading, setAdminPaymentsLoading] = useState(false);
  const [adminPaymentActionKey, setAdminPaymentActionKey] = useState('');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [depositStatusFilter, setDepositStatusFilter] = useState('');
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState('');
  const [manualDepositUserId, setManualDepositUserId] = useState('');
  const [manualDepositAmountWkp, setManualDepositAmountWkp] = useState('');
  const [manualDepositNote, setManualDepositNote] = useState('');

  // Bank Transfer Admin state
  const [bankTransferRequests, setBankTransferRequests] = useState([]);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);
  const [bankTransferFilter, setBankTransferFilter] = useState('paid');
  const [bankTransferActionId, setBankTransferActionId] = useState(null);
  const [bankSettingsData, setBankSettingsData] = useState(null);
  const [bankSettingsLoading, setBankSettingsLoading] = useState(false);
  const [bankSettingsSaved, setBankSettingsSaved] = useState(false);
  const [adminMessages, setAdminMessages] = useState([]);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageAudienceType, setMessageAudienceType] = useState('all');
  const [messageSelectedUserIds, setMessageSelectedUserIds] = useState('');
  const [messageSendInApp, setMessageSendInApp] = useState(true);
  const [messageSendEmail, setMessageSendEmail] = useState(false);
  const [messageSendSms, setMessageSendSms] = useState(false);
  const [messageDismissible, setMessageDismissible] = useState(true);
  const [messageExpiresAt, setMessageExpiresAt] = useState('');
  const [messageEmailTemplate, setMessageEmailTemplate] = useState('announcement');
  const [messageEmailHtmlMode, setMessageEmailHtmlMode] = useState('template');
  const [messageEmailCustomHtml, setMessageEmailCustomHtml] = useState('');
  const [messageEmailCtaLabel, setMessageEmailCtaLabel] = useState('');
  const [messageEmailCtaUrl, setMessageEmailCtaUrl] = useState('');
  const [messageEmailProvider, setMessageEmailProvider] = useState('default');

  // TOTP 2FA state
  const [totpEnabled, setTotpEnabled] = useState(null);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [totpSetupCode, setTotpSetupCode] = useState('');
  const [totpDisableCode, setTotpDisableCode] = useState('');
  const [totpMessage, setTotpMessage] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  const loadTotpStatus = useCallback(async () => {
    try {
      const res = await authAPI.totpStatus();
      setTotpEnabled(res.data.totp_enabled);
    } catch {
      setTotpEnabled(false);
    }
  }, []);

  const handleTotpSetupStart = async () => {
    setTotpError(''); setTotpMessage(''); setTotpLoading(true);
    try {
      const res = await authAPI.totpSetup();
      setTotpSetupData(res.data);
    } catch (err) {
      setTotpError(err?.response?.data?.error || 'Failed to start TOTP setup');
    } finally { setTotpLoading(false); }
  };

  const handleTotpVerifySetup = async (e) => {
    e.preventDefault(); setTotpError(''); setTotpLoading(true);
    try {
      await authAPI.totpVerifySetup(totpSetupCode.replace(/\s/g, ''));
      setTotpEnabled(true); setTotpSetupData(null); setTotpSetupCode('');
      setTotpMessage('2FA enabled. Your account is now protected by TOTP.');
    } catch (err) {
      setTotpError(err?.response?.data?.error || 'Invalid code');
    } finally { setTotpLoading(false); }
  };

  const handleTotpDisable = async (e) => {
    e.preventDefault(); setTotpError(''); setTotpLoading(true);
    try {
      await authAPI.totpDisable(totpDisableCode.replace(/\s/g, ''));
      setTotpEnabled(false); setTotpDisableCode('');
      setTotpMessage('2FA disabled.');
    } catch (err) {
      setTotpError(err?.response?.data?.error || 'Invalid code');
    } finally { setTotpLoading(false); }
  };

  const loadBaseData = async () => {
    try {
      setError('');

      const [statsRes] = await Promise.all([
        adminAPI.getStats(),
      ]);

      setStats(statsRes?.data?.stats || null);

      if (statsRes?.data?.stats?.prizeDistribution || Number.isFinite(Number(statsRes?.data?.stats?.chargePercent))) {
        setSettings((prev) => ({
          chargePercent: Number.isFinite(Number(statsRes?.data?.stats?.chargePercent))
            ? Number(statsRes.data.stats.chargePercent)
            : prev.chargePercent,
          minimumWager: Number.isFinite(Number(statsRes?.data?.stats?.minimumWager))
            ? Number(statsRes.data.stats.minimumWager)
            : prev.minimumWager,
          minimumDeposit: Number.isFinite(Number(statsRes?.data?.stats?.minimumDeposit))
            ? Number(statsRes.data.stats.minimumDeposit)
            : prev.minimumDeposit,
          minimumWithdrawal: Number.isFinite(Number(statsRes?.data?.stats?.minimumWithdrawal))
            ? Number(statsRes.data.stats.minimumWithdrawal)
            : prev.minimumWithdrawal,
          onlineMembersMode: statsRes?.data?.stats?.onlineMembersMode || prev.onlineMembersMode || 'manual',
          onlineMembers: Number.isFinite(Number(statsRes?.data?.stats?.onlineMembers))
            ? Number(statsRes.data.stats.onlineMembers)
            : prev.onlineMembers,
          signupCredit: Number.isFinite(Number(statsRes?.data?.stats?.signupCredit))
            ? Number(statsRes.data.stats.signupCredit)
            : prev.signupCredit,
          referralBonusPercent: Number.isFinite(Number(statsRes?.data?.stats?.referralBonusPercent))
            ? Number(statsRes.data.stats.referralBonusPercent)
            : prev.referralBonusPercent,
          prizeDistribution: statsRes?.data?.stats?.prizeDistribution || prev.prizeDistribution
        }));
      }

      const settingsRes = await adminAPI.getSettings();
      if (settingsRes?.data?.settings) {
        setSettings(settingsRes.data.settings);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load admin data');
    }
  };

  const loadUsers = async (page = 1, searchText = search) => {
    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * MEMBER_PAGE_SIZE;
      const usersRes = await adminAPI.listUsers({
        limit: MEMBER_PAGE_SIZE,
        offset,
        search: searchText
      });
      const rows = usersRes?.data?.users || [];
      setUsers(rows);
      setUsersPage(page);
      setUsersHasMore(rows.length === MEMBER_PAGE_SIZE);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * TRANSACTION_PAGE_SIZE;
      const txRes = await adminAPI.listTransactions({ limit: TRANSACTION_PAGE_SIZE, offset });
      const entries = txRes?.data?.entries || [];
      setTransactions(entries);
      setTransactionsPage(page);
      setTransactionsHasMore(entries.length === TRANSACTION_PAGE_SIZE);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingWithdrawals = async () => {
    try {
      setPendingWithdrawalsLoading(true);
      const response = await paymentAPI.listPendingWithdrawals({ limit: 100 });
      setPendingWithdrawals(Array.isArray(response?.data?.withdrawals) ? response.data.withdrawals : []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load pending withdrawals');
    } finally {
      setPendingWithdrawalsLoading(false);
    }
  };

  const loadAdminDeposits = async ({
    page = adminDepositsPage,
    searchText = paymentSearch,
    status = depositStatusFilter,
  } = {}) => {
    try {
      setAdminPaymentsLoading(true);
      const offset = (page - 1) * ADMIN_PAYMENT_PAGE_SIZE;
      const depositsRes = await paymentAPI.listAdminDeposits({
        limit: ADMIN_PAYMENT_PAGE_SIZE,
        offset,
        search: searchText,
        status,
      });
      const rows = Array.isArray(depositsRes?.data?.deposits) ? depositsRes.data.deposits : [];
      setAdminDeposits(rows);
      setAdminDepositsPage(page);
      setAdminDepositsHasMore(rows.length === ADMIN_PAYMENT_PAGE_SIZE);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load admin deposits');
    } finally {
      setAdminPaymentsLoading(false);
    }
  };

  const loadAdminWithdrawals = async ({
    page = adminWithdrawalsPage,
    searchText = paymentSearch,
    status = withdrawalStatusFilter,
  } = {}) => {
    try {
      setAdminPaymentsLoading(true);
      const offset = (page - 1) * ADMIN_PAYMENT_PAGE_SIZE;
      const withdrawalsRes = await paymentAPI.listAdminWithdrawals({
        limit: ADMIN_PAYMENT_PAGE_SIZE,
        offset,
        search: searchText,
        status,
      });
      const rows = Array.isArray(withdrawalsRes?.data?.withdrawals) ? withdrawalsRes.data.withdrawals : [];
      setAdminWithdrawals(rows);
      setAdminWithdrawalsPage(page);
      setAdminWithdrawalsHasMore(rows.length === ADMIN_PAYMENT_PAGE_SIZE);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load admin withdrawals');
    } finally {
      setAdminPaymentsLoading(false);
    }
  };

  const loadAdminMessages = async () => {
    try {
      const response = await adminAPI.listMessages({ limit: 100 });
      setAdminMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load admin messages');
    }
  };

  const refreshAll = async () => {
    setSuccessMessage('');
    await loadBaseData();
      await Promise.all([
        loadUsers(usersPage, search),
        loadTransactions(transactionsPage),
        loadPendingWithdrawals(),
        loadAdminDeposits(),
        loadAdminWithdrawals(),
        loadAdminMessages()
      ]);
    };

  const fetchUserTransactions = async (userId, page = 1, category = 'all', search = '') => {
    if (!userId) return;
    try {
      setUserTxLoading(true);
      const response = await adminAPI.getUserTransactions(userId, {
        page,
        limit: 20,
        category: category === 'all' ? '' : category,
        search
      });
      setUserTxData({
        transactions: response?.data?.transactions || [],
        total: response?.data?.total || 0,
        totalPages: response?.data?.totalPages || 1,
        categories: response?.data?.categories || [],
        categorySummary: response?.data?.categorySummary || []
      });
    } catch (err) {
      console.error('Failed to fetch user transactions:', err);
    } finally {
      setUserTxLoading(false);
    }
  };

  const viewUser = async (userId) => {
    try {
      setLoading(true);
      const response = await adminAPI.getUserDetails(userId);
      setSelectedUser(response?.data?.user || null);
      setSelectedUserTransactions(response?.data?.transactions || []);
      setUserTxCategory('all');
      setUserTxSearch('');
      setUserTxPage(1);
      setProfileOpen(true);
      await fetchUserTransactions(userId, 1, 'all', '');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load member profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUserTxCategoryChange = async (cat) => {
    setUserTxCategory(cat);
    setUserTxPage(1);
    if (selectedUser?.id) {
      await fetchUserTransactions(selectedUser.id, 1, cat, userTxSearch);
    }
  };

  const handleUserTxPageChange = async (newPage) => {
    setUserTxPage(newPage);
    if (selectedUser?.id) {
      await fetchUserTransactions(selectedUser.id, newPage, userTxCategory, userTxSearch);
    }
  };

  const handleUserTxSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    setUserTxPage(1);
    if (selectedUser?.id) {
      await fetchUserTransactions(selectedUser.id, 1, userTxCategory, userTxSearch);
    }
  };

  const exportUserTransactionsCSV = (user, txList) => {
    if (!txList || !txList.length) return;
    const headers = ['Transaction ID', 'Date & Time', 'Category', 'Delta (WKP)', 'Balance After', 'Game', 'Description', 'Reference Type', 'Reference ID'];
    const rows = txList.map((t) => [
      t.id,
      new Date(t.created_at).toLocaleString(),
      t.category,
      t.delta,
      t.balance_after,
      t.game || '',
      `"${String(t.description || '').replace(/"/g, '""')}"`,
      t.reference_type || '',
      t.reference_id || ''
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `transactions_${user?.username || 'user'}_${user?.id || 'id'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleBlock = async (user) => {
    try {
      setLoading(true);
      if (user.is_blocked) {
        await adminAPI.unblockUser(user.id);
      } else {
        const reason = window.prompt('Reason for blocking this member (optional):', 'Admin action') || '';
        await adminAPI.blockUser(user.id, reason);
      }
      setSuccessMessage(`Member ${user.is_blocked ? 'unblocked' : 'blocked'} successfully.`);
      await loadUsers(usersPage, search);
      await loadBaseData();
      if (selectedUser?.id === user.id) {
        await viewUser(user.id);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update member status');
      setLoading(false);
    }
  };

  const toggleRole = async (user) => {
    try {
      setLoading(true);
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      const actionName = newRole === 'admin' ? 'make this user an admin' : 'remove admin privileges from this user';
      if (!window.confirm(`Are you sure you want to ${actionName}?`)) {
        setLoading(false);
        return;
      }
      await adminAPI.updateUserRole(user.id, newRole);
      setSuccessMessage(`User role updated to ${newRole} successfully.`);
      await loadUsers(usersPage, search);
      if (selectedUser?.id === user.id) {
        await viewUser(user.id);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailManual = async (user) => {
    try {
      setLoading(true);
      await adminAPI.verifyUserEmail(user.id);
      setSuccessMessage('User email verified successfully.');
      await loadUsers(usersPage, search);
      if (selectedUser?.id === user.id) {
        await viewUser(user.id);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to verify user email');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceAdjust = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const targetUserId = Number(balanceTargetUserId);
      const rawDelta = Math.abs(Math.trunc(Number(balanceDelta)));
      if (!Number.isInteger(targetUserId) || targetUserId <= 0) {
        throw new Error('Target user ID must be a valid number');
      }
      if (!Number.isFinite(rawDelta) || rawDelta === 0) {
        throw new Error('Amount must be a non-zero positive integer');
      }

      const res = await adminAPI.adjustBalance(targetUserId, rawDelta, balanceReason, {
        actionType: balanceMode,
        forceDeduct: balanceMode === 'deduct'
      });
      setBalanceDelta('');
      setBalanceReason('');
      setSuccessMessage(res?.data?.message || 'Balance updated successfully.');
      await loadUsers(usersPage, search);
      await loadTransactions(1);
      await loadAdminWithdrawals();
      await loadBaseData();
      if (selectedUser?.id === targetUserId) {
        await viewUser(targetUserId);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to adjust balance');
    } finally {
      setLoading(false);
    }
  };

  const handleDeductCoinsFromUser = async (targetUserId, defaultAmount = 0, defaultReason = '') => {
    const amountStr = window.prompt(
      `Enter WikiPenny amount to DEDUCT/REMOVE from user #${targetUserId}:`,
      defaultAmount ? String(defaultAmount) : ''
    );
    if (!amountStr) return;
    const amountWkp = Math.abs(Math.trunc(Number(amountStr)));
    if (!amountWkp || Number.isNaN(amountWkp)) {
      alert('Please enter a valid positive number of WikiPenny to deduct.');
      return;
    }

    const reason = window.prompt('Reason for deduction (optional):', defaultReason || 'admin_manual_deduction') || 'admin_manual_deduction';

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const res = await adminAPI.adjustBalance(targetUserId, -amountWkp, reason, { actionType: 'deduct', forceDeduct: true });
      setSuccessMessage(res?.data?.message || `Successfully deducted ${amountWkp} WKP from user #${targetUserId}.`);
      await loadUsers(usersPage, search);
      await loadTransactions(1);
      await loadAdminWithdrawals();
      await loadBaseData();
      if (selectedUser?.id === targetUserId) {
        await viewUser(targetUserId);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to deduct balance');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoinsToUser = async (targetUserId) => {
    const amountStr = window.prompt(`Enter WikiPenny amount to ADD/CREDIT to user #${targetUserId}:`, '');
    if (!amountStr) return;
    const amountWkp = Math.abs(Math.trunc(Number(amountStr)));
    if (!amountWkp || Number.isNaN(amountWkp)) {
      alert('Please enter a valid positive number of WikiPenny to add.');
      return;
    }

    const reason = window.prompt('Reason for addition (optional):', 'admin_manual_credit') || 'admin_manual_credit';

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const res = await adminAPI.adjustBalance(targetUserId, amountWkp, reason, { actionType: 'add' });
      setSuccessMessage(res?.data?.message || `Successfully added ${amountWkp} WKP to user #${targetUserId}.`);
      await loadUsers(usersPage, search);
      await loadTransactions(1);
      await loadBaseData();
      if (selectedUser?.id === targetUserId) {
        await viewUser(targetUserId);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to add balance');
    } finally {
      setLoading(false);
    }
  };

  const updateDistributionCell = (playerCount, position, value) => {
    setSettings((prev) => {
      const current = Array.isArray(prev?.prizeDistribution?.[playerCount])
        ? [...prev.prizeDistribution[playerCount]]
        : Array.from({ length: playerCount }).map(() => 0);
      current[position] = Number(value);
      return {
        ...prev,
        prizeDistribution: {
          ...prev.prizeDistribution,
          [playerCount]: current
        }
      };
    });
  };

  const getDistributionTotal = (playerCount) => {
    const distribution = settings?.prizeDistribution?.[playerCount] || [];
    return distribution.reduce((sum, value) => sum + Number(value || 0), 0);
  };

  const isDistributionValid = [2, 3, 4, 5].every((playerCount) => {
    const total = getDistributionTotal(playerCount);
    return Math.abs(total - 100) < 0.001;
  });

  const saveSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const payload = {
        chargePercent: Number(settings.chargePercent),
        minimumWager: Math.trunc(Number(settings.minimumWager || 0)),
        minimumDeposit: Math.trunc(Number(settings.minimumDeposit || 0)),
        minimumWithdrawal: Math.trunc(Number(settings.minimumWithdrawal || 0)),
        onlineMembersMode: settings.onlineMembersMode || 'manual',
        onlineMembers: Math.trunc(Number(settings.onlineMembers || 0)),
        onlineMembersRandomMin: Math.trunc(Number(settings.onlineMembersRandomMin || 20)),
        onlineMembersRandomMax: Math.trunc(Number(settings.onlineMembersRandomMax || 200)),
        onlineMembersRandomIntervalSeconds: Math.trunc(Number(settings.onlineMembersRandomIntervalSeconds || 30)),
        signupCredit: Math.trunc(Number(settings.signupCredit || 0)),
        referralBonusPercent: Number(settings.referralBonusPercent),
        prizeDistribution: settings.prizeDistribution
      };
      const response = await adminAPI.updateSettings(payload);
      if (response?.data?.settings) {
        setSettings(response.data.settings);
      }
      setSuccessMessage('Platform settings saved successfully.');
      await loadBaseData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const updateWithdrawalModeOnly = async (mode) => {
    try {
      setLoading(true);
      setError('');
      await adminAPI.updateWithdrawalMode(mode);
      setSettings((prev) => ({ ...prev, withdrawalMode: mode }));
      setSuccessMessage(`Withdrawal mode updated to ${mode}.`);
      await loadBaseData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update withdrawal mode');
    } finally {
      setLoading(false);
    }
  };

  const approvePendingWithdrawal = async (entry) => {
    const withdrawalId = entry?.id;
    const provider = String(entry?.provider || '').toLowerCase();
    const verificationCode = provider === 'nowpayments'
      ? window.prompt('Enter NOWPayments 2FA verification code to approve this payout:')
      : '';

    if (provider === 'nowpayments' && !String(verificationCode || '').trim()) {
      setError('NOWPayments approval requires a 2FA verification code.');
      return;
    }

    try {
      setWithdrawActionId(withdrawalId);
      const res = await paymentAPI.approveWithdrawal(withdrawalId, verificationCode);
      const payoutError = String(res?.data?.payoutError || '').trim();
      if (payoutError) {
        setError(payoutError);
      } else {
        setSuccessMessage('Withdrawal approved successfully.');
      }
      await Promise.all([loadPendingWithdrawals(), loadTransactions(1), loadBaseData()]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to approve withdrawal');
    } finally {
      setWithdrawActionId(null);
    }
  };

  const rejectPendingWithdrawal = async (withdrawalId) => {
    try {
      setWithdrawActionId(withdrawalId);
      await paymentAPI.rejectWithdrawal(withdrawalId, 'rejected_by_admin_from_dashboard');
      setSuccessMessage('Withdrawal rejected successfully.');
      await Promise.all([loadPendingWithdrawals(), loadTransactions(1), loadBaseData()]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to reject withdrawal');
    } finally {
      setWithdrawActionId(null);
    }
  };

  const handleManualDepositSubmit = async (event) => {
    event.preventDefault();
    try {
      setAdminPaymentActionKey('manual-deposit');
      await paymentAPI.manualAdminDeposit({
        userId: Number(manualDepositUserId),
        amountWkp: Number(manualDepositAmountWkp),
        note: manualDepositNote,
      });
      setManualDepositUserId('');
      setManualDepositAmountWkp('');
      setManualDepositNote('');
      setSuccessMessage('Manual WKP deposit completed successfully.');
      await Promise.all([loadAdminDeposits({ page: adminDepositsPage }), loadAdminWithdrawals({ page: adminWithdrawalsPage }), loadTransactions(1), loadBaseData(), loadUsers(usersPage, search)]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to create manual deposit');
    } finally {
      setAdminPaymentActionKey('');
    }
  };

  // ── Bank Transfer Admin Handlers ──────────────────────────────────
  const loadBankTransferRequests = async (filter = bankTransferFilter) => {
    setBankTransferLoading(true);
    try {
      const res = await paymentAPI.adminListBankTransfers({ status: filter, page: 1 });
      setBankTransferRequests(Array.isArray(res?.data?.requests) ? res.data.requests : []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load bank transfer requests');
    } finally {
      setBankTransferLoading(false);
    }
  };

  const handleApproveBankTransfer = async (req) => {
    const note = window.prompt(`Optional note for approving ${req.reference}:`, '') || '';
    setBankTransferActionId(req.id);
    try {
      await paymentAPI.adminApproveBankTransfer(req.id, { note });
      setSuccessMessage(`✅ Bank transfer ${req.reference} approved. ₦${Number(req.amountNaira).toLocaleString()} → ${Number(req.amountWkp).toLocaleString()} WKP credited to ${req.username}.`);
      await loadBankTransferRequests();
      await loadBaseData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to approve bank transfer');
    } finally {
      setBankTransferActionId(null);
    }
  };

  const handleRejectBankTransfer = async (req) => {
    const reason = window.prompt(`Rejection reason for ${req.reference}:`, 'Transfer not received') || 'Transfer not received';
    setBankTransferActionId(req.id);
    try {
      await paymentAPI.adminRejectBankTransfer(req.id, { reason });
      setSuccessMessage(`Bank transfer ${req.reference} rejected.`);
      await loadBankTransferRequests();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to reject bank transfer');
    } finally {
      setBankTransferActionId(null);
    }
  };

  const loadBankSettings = async () => {
    setBankSettingsLoading(true);
    try {
      const res = await paymentAPI.adminGetBankSettings();
      setBankSettingsData(res?.data || {});
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load bank settings');
    } finally {
      setBankSettingsLoading(false);
    }
  };

  const handleSaveBankSettings = async (e) => {
    e.preventDefault();
    setBankSettingsLoading(true);
    setBankSettingsSaved(false);
    try {
      const res = await paymentAPI.adminUpdateBankSettings(bankSettingsData);
      setBankSettingsData(res?.data?.settings || bankSettingsData);
      setBankSettingsSaved(true);
      setSuccessMessage('✅ Bank settings saved successfully.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to save bank settings');
    } finally {
      setBankSettingsLoading(false);
    }
  };

  const handleAdminDepositStatusUpdate = async (deposit, status) => {
    const note = window.prompt(`Optional admin note for deposit ${deposit.reference}:`, '');
    const payload = { status, note: note || undefined };
    if (status === 'success' || status === 'partial_success') {
      const suggestedReceivedUsd = Number(deposit?.receivedAmount || deposit?.creditedUsdAmount || deposit?.amount || 0);
      const receivedUsdAmount = window.prompt('Amount received from user in USDT/USD:', String(suggestedReceivedUsd || ''));
      if (receivedUsdAmount === null) return;
      const normalizedReceivedUsd = Number(receivedUsdAmount);
      if (!Number.isFinite(normalizedReceivedUsd) || normalizedReceivedUsd <= 0) {
        setError('Received USDT/USD amount must be greater than 0.');
        return;
      }
      payload.receivedUsdAmount = normalizedReceivedUsd;
      const providerStatus = window.prompt('Provider/payment status label (optional):', String(deposit?.providerStatus || 'confirmed_manually'));
      if (providerStatus === null) return;
      payload.providerStatus = providerStatus || undefined;
      const txId = window.prompt('Provider transaction ID / proof reference (optional):', '');
      if (txId === null) return;
      payload.providerTransactionId = txId || undefined;
    }

    try {
      setAdminPaymentActionKey(`deposit-${deposit.id}-${status}`);
      await paymentAPI.updateAdminDepositStatus(deposit.id, payload);
      setSuccessMessage(`Deposit ${deposit.reference} updated to ${status}.`);
      await Promise.all([loadAdminDeposits({ page: adminDepositsPage }), loadTransactions(1), loadBaseData(), loadUsers(usersPage, search)]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update deposit status');
    } finally {
      setAdminPaymentActionKey('');
    }
  };

  const handleAdminWithdrawalStatusUpdate = async (withdrawal, status) => {
    const note = window.prompt(`Optional admin note for withdrawal ${withdrawal.reference}:`, '');
    if (note === null) return;
    const payload = { status, note: note || undefined };
    if (status === 'success' || status === 'processing') {
      const providerPayoutId = window.prompt('Provider payout ID / manual transfer reference (optional):', String(withdrawal?.provider_payout_id || ''));
      if (providerPayoutId === null) return;
      payload.providerPayoutId = providerPayoutId || undefined;
    }
    if (status === 'failed' || status === 'rejected') {
      const reason = window.prompt('Reason for failed/rejected status:', String(withdrawal?.failed_reason || note || 'manual_admin_update'));
      if (reason === null) return;
      payload.reason = reason || 'manual_admin_update';
    }

    try {
      setAdminPaymentActionKey(`withdrawal-${withdrawal.id}-${status}`);
      await paymentAPI.updateAdminWithdrawalStatus(withdrawal.id, payload);
      setSuccessMessage(`Withdrawal ${withdrawal.reference} updated to ${status}.`);
      await Promise.all([loadAdminWithdrawals({ page: adminWithdrawalsPage }), loadPendingWithdrawals(), loadTransactions(1), loadBaseData(), loadUsers(usersPage, search)]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update withdrawal status');
    } finally {
      setAdminPaymentActionKey('');
    }
  };

  const exportTransactionsCsv = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.exportTransactionsCsv({ limit: 5000 });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to export transactions CSV');
    } finally {
      setLoading(false);
    }
  };

  const exportUsersCsv = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.exportUsersCsv({ limit: 10000, search });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to export users CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdminMessage = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const payload = {
        title: messageTitle,
        body: messageBody,
        audienceType: messageAudienceType,
        selectedUserIds: messageSelectedUserIds,
        sendInApp: messageSendInApp,
        sendEmail: messageSendEmail,
        sendSms: messageSendSms,
        dismissible: messageDismissible,
        expiresAt: messageExpiresAt || undefined,
        emailTemplate: messageEmailTemplate,
        emailHtmlMode: messageEmailHtmlMode,
        emailCustomHtml: messageEmailCustomHtml || undefined,
        emailCtaLabel: messageEmailCtaLabel || undefined,
        emailCtaUrl: messageEmailCtaUrl || undefined,
        emailProvider: messageEmailProvider,
      };
      await adminAPI.createMessage(payload);
      setSuccessMessage('Admin message sent successfully.');
      setMessageTitle('');
      setMessageBody('');
      setMessageSelectedUserIds('');
      setMessageSendEmail(false);
      setMessageSendSms(false);
      setMessageSendInApp(true);
      setMessageDismissible(true);
      setMessageExpiresAt('');
      setMessageEmailTemplate('announcement');
      setMessageEmailHtmlMode('template');
      setMessageEmailCustomHtml('');
      setMessageEmailCtaLabel('');
      setMessageEmailCtaUrl('');
      setMessageEmailProvider('default');
      await loadAdminMessages();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to send admin message');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllAdminMessages = async () => {
    if (!window.confirm('Are you sure you want to delete ALL player home dashboard messages? This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const res = await adminAPI.deleteAllMessages();
      setSuccessMessage(res?.data?.message || 'All player home dashboard messages have been deleted.');
      await loadAdminMessages();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to delete dashboard messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdminMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      await adminAPI.deleteMessage(messageId);
      setSuccessMessage('Dashboard message deleted successfully.');
      await loadAdminMessages();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to delete message');
    } finally {
      setLoading(false);
    }
  };

  const runEscrowReconcile = async ({ execute, silent = false, gameOverride = null }) => {
    try {
      setLoading(true);
      if (!silent) {
        setError('');
        setSuccessMessage('');
        setReconcileSummary(null);
      }

      const selectedGame = gameOverride || reconcileGame;
      const response = await adminAPI.reconcileStaleEscrow({
        execute,
        minutes: Number.isFinite(Number(reconcileMinutes)) ? Number(reconcileMinutes) : 0,
        limit: Number(reconcileLimit) || 100,
        game: selectedGame === 'all' ? '' : selectedGame,
      });

      const payload = response?.data || {};
      setReconcileSummary(payload);

      const mode = payload?.mode || (execute ? 'execute' : 'dry-run');
      const candidateCount = Number(payload?.candidateCount || 0);
      const totalRefund = Number(payload?.summary?.totalRefund || 0);
      const appliedCount = Number(payload?.appliedCount || payload?.summary?.refunded || 0);

      if (execute && mode !== 'execute') {
        setError('Execute was requested but response came back as dry-run. Please restart backend and try again.');
      }

      if (!silent) {
        setSuccessMessage(
          `${mode === 'dry-run' ? 'Dry run complete' : 'Escrow recovery complete'}: ${candidateCount} candidate(s), total refund ${totalRefund}, applied refunds ${appliedCount}.`
        );
      }

      await Promise.all([loadBaseData(), loadTransactions(1)]);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.error || err.message || 'Failed to run stale escrow reconcile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadBaseData();
      await Promise.all([loadUsers(1), loadTransactions(1), loadPendingWithdrawals(), loadAdminMessages(), loadTotpStatus()]);

      await runEscrowReconcile({ execute: false, silent: true, gameOverride: 'all' });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTotpStatus]);

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="admin-header-section">
          <div className="header-left">
            <div className="header-icon">🛡️</div>
            <div>
              <h1 className="page-title">Admin Dashboard</h1>
              <p className="page-subtitle">Manage members, transactions, and platform settings</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              ← Back to Dashboard
            </button>
            <button className="refresh-btn" onClick={refreshAll} disabled={loading}>
              ⟳ Refresh
            </button>
            <button className="logout-btn" onClick={logout}>
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}
        
        {successMessage && (
          <div className="success-alert">
            <span className="success-icon">✓</span>
            <span>{successMessage}</span>
            <button className="success-close" onClick={() => setSuccessMessage('')}>×</button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-label">Total Members</div>
              <div className="stat-value">{stats?.totalMembers ?? '—'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🟢</div>
            <div className="stat-info">
              <div className="stat-label">Online Now</div>
              <div className="stat-value">{stats?.currentlyOnline ?? '—'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🚫</div>
            <div className="stat-info">
              <div className="stat-label">Blocked Members</div>
              <div className="stat-value">{stats?.blockedMembers ?? '—'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-label">Total Transactions</div>
              <div className="stat-value">{stats?.totalTransactions ?? '—'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-label">Platform Profit</div>
              <div className="stat-value">{stats?.platformProfitTotal ?? '—'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💎</div>
            <div className="stat-info">
              <div className="stat-label">Total Deposits</div>
              <div className="stat-value">{stats?.totalDeposits ?? '—'}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            <span className="tab-icon">👥</span>
            Members
          </button>
          <button
            className={`admin-tab ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <span className="tab-icon">📜</span>
            Transactions
          </button>
          <button
            className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <span className="tab-icon">💳</span>
            Payments
          </button>
          <button
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <span className="tab-icon">⚙️</span>
            Settings
          </button>
          <button
            className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <span className="tab-icon">📣</span>
            Messages
          </button>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="admin-card">
            <div className="card-header">
              <h2 className="card-title">Registered Members</h2>
              <button className="export-btn" onClick={exportUsersCsv} disabled={loading} style={{ marginLeft: 'auto', marginRight: '1rem' }}>
                📥 Export Users CSV
              </button>
              <div className="search-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  placeholder="Search username or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="search-btn" onClick={() => loadUsers(1, search)} disabled={loading}>
                  Search
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Balance</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <div className="user-name-cell">
                          <span className={`online-dot ${user.is_online ? 'online' : ''}`}></span>
                          {user.username}
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td><span className={`role-badge ${user.role}`}>{user.role}</span></td>
                      <td>
                        <span className={`status-badge ${user.is_blocked ? 'blocked' : (user.is_online ? 'online' : 'offline')}`}>
                          {user.is_blocked ? 'Blocked' : (user.is_online ? 'Online' : 'Offline')}
                        </span>
                      </td>
                      <td className="balance-cell">{user.balance}</td>
                      <td className="date-cell">{formatDateTime(user.last_login_at)}</td>
                      <td className="actions-cell">
                        <button className="action-btn view" onClick={() => viewUser(user.id)} disabled={loading}>
                          View
                        </button>
                        <button className="action-btn approve" onClick={() => handleAddCoinsToUser(user.id)} disabled={loading} style={{ marginLeft: '4px' }}>
                          + Add
                        </button>
                        <button className="action-btn reject" onClick={() => handleDeductCoinsFromUser(user.id)} disabled={loading} style={{ marginLeft: '4px' }}>
                          - Deduct
                        </button>
                        <button
                          className={`action-btn ${user.role === 'admin' ? 'block' : 'approve'}`}
                          onClick={() => toggleRole(user)}
                          disabled={loading}
                          style={{ marginLeft: '4px' }}
                        >
                          {user.role === 'admin' ? 'Disable Admin' : 'Make Admin'}
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            className={`action-btn ${user.is_blocked ? 'unblock' : 'block'}`}
                            onClick={() => toggleBlock(user)}
                            disabled={loading}
                            style={{ marginLeft: '4px' }}
                          >
                            {user.is_blocked ? 'Unblock' : 'Block'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="8" className="empty-cell">No members found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button
                className="pagination-btn"
                onClick={() => loadUsers(usersPage - 1, search)}
                disabled={loading || usersPage <= 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">Page {usersPage}</span>
              <button
                className="pagination-btn"
                onClick={() => loadUsers(usersPage + 1, search)}
                disabled={loading || !usersHasMore}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="admin-card">
            <div className="card-header">
              <h2 className="card-title">All Transactions</h2>
              <button className="export-btn" onClick={exportTransactionsCsv} disabled={loading}>
                📥 Export CSV
              </button>
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((entry) => (
                    <tr key={entry.id}>
                      <td className="date-cell">{formatDateTime(entry.created_at)}</td>
                      <td>{entry.username}</td>
                      <td>
                        <span className={`category-badge ${entry.category}`}>
                          {entry.category}
                        </span>
                      </td>
                      <td className={`amount-cell ${entry.delta > 0 ? 'positive' : entry.delta < 0 ? 'negative' : ''}`}>
                        {entry.delta > 0 ? '+' : ''}{entry.delta}
                      </td>
                      <td className="balance-cell">{entry.balance_after}</td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan="5" className="empty-cell">No transactions found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button
                className="pagination-btn"
                onClick={() => loadTransactions(transactionsPage - 1)}
                disabled={loading || transactionsPage <= 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">Page {transactionsPage}</span>
              <button
                className="pagination-btn"
                onClick={() => loadTransactions(transactionsPage + 1)}
                disabled={loading || !transactionsHasMore}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <>
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Manual WKP Deposit</h2>
                <p className="card-subtitle">Credit a member after confirming off-platform payment</p>
              </div>
              <form className="balance-form" onSubmit={handleManualDepositSubmit}>
                <div className="form-group">
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Target User ID"
                    value={manualDepositUserId}
                    onChange={(e) => setManualDepositUserId(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Amount in WKP"
                    value={manualDepositAmountWkp}
                    onChange={(e) => setManualDepositAmountWkp(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Note / proof reference"
                    value={manualDepositNote}
                    onChange={(e) => setManualDepositNote(e.target.value)}
                  />
                </div>
                <button type="submit" className="save-btn" disabled={adminPaymentActionKey === 'manual-deposit'}>
                  Credit WKP
                </button>
              </form>
            </div>

            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Payment Operations</h2>
                <div className="search-wrapper">
                  <span className="search-icon">Search</span>
                  <input
                    className="search-input"
                    placeholder="Search reference, username, email, wallet"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                  />
                  <button
                    className="search-btn"
                    onClick={() => {
                      if (paymentsViewTab === 'deposits') {
                        loadAdminDeposits({ page: 1, searchText: paymentSearch, status: depositStatusFilter });
                      } else if (paymentsViewTab === 'withdrawals') {
                        loadAdminWithdrawals({ page: 1, searchText: paymentSearch, status: withdrawalStatusFilter });
                      } else if (paymentsViewTab === 'bank_transfers') {
                        loadBankTransferRequests(bankTransferFilter);
                      }
                    }}
                    disabled={adminPaymentsLoading || bankTransferLoading}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="admin-tabs" style={{ marginBottom: 16 }}>
                <button
                  className={`admin-tab ${paymentsViewTab === 'deposits' ? 'active' : ''}`}
                  onClick={() => setPaymentsViewTab('deposits')}
                >
                  <span className="tab-icon">📥</span>
                  Deposits
                </button>
                <button
                  className={`admin-tab ${paymentsViewTab === 'bank_transfers' ? 'active' : ''}`}
                  onClick={() => { setPaymentsViewTab('bank_transfers'); loadBankTransferRequests(); if (!bankSettingsData) loadBankSettings(); }}
                >
                  🏦 Bank Transfers
                </button>
                <button
                  className={`admin-tab ${paymentsViewTab === 'withdrawals' ? 'active' : ''}`}
                  onClick={() => setPaymentsViewTab('withdrawals')}
                >
                  <span className="tab-icon">📤</span>
                  Withdrawals
                </button>
              </div>

              <div className="settings-grid" style={{ marginBottom: 16 }}>
                {paymentsViewTab === 'deposits' && (
                  <div className="setting-item">
                    <label>Deposit Status</label>
                    <select value={depositStatusFilter} onChange={(e) => setDepositStatusFilter(e.target.value)}>
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="success">Success</option>
                      <option value="partial_success">Partial</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                )}
                {paymentsViewTab === 'withdrawals' && (
                  <div className="setting-item">
                    <label>Withdrawal Status</label>
                    <select value={withdrawalStatusFilter} onChange={(e) => setWithdrawalStatusFilter(e.target.value)}>
                      <option value="">All</option>
                      <option value="pending_approval">Pending Approval</option>
                      <option value="approved">Approved</option>
                      <option value="processing">Processing</option>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                )}
                {paymentsViewTab === 'bank_transfers' && (
                  <div className="setting-item">
                    <label>Request Status</label>
                    <select value={bankTransferFilter} onChange={(e) => { setBankTransferFilter(e.target.value); loadBankTransferRequests(e.target.value); }}>
                      <option value="paid">Paid (Awaiting Approval)</option>
                      <option value="pending">Pending (Unpaid)</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="admin-actions" style={{ marginBottom: 16 }}>
                <button
                  className="save-btn"
                  onClick={() => {
                    if (paymentsViewTab === 'deposits') {
                      loadAdminDeposits({ page: 1, searchText: paymentSearch, status: depositStatusFilter });
                    } else if (paymentsViewTab === 'withdrawals') {
                      loadAdminWithdrawals({ page: 1, searchText: paymentSearch, status: withdrawalStatusFilter });
                    } else if (paymentsViewTab === 'bank_transfers') {
                      loadBankTransferRequests(bankTransferFilter);
                    }
                  }}
                  disabled={adminPaymentsLoading || bankTransferLoading}
                >
                  Apply Filters / Refresh
                </button>
              </div>

              {paymentsViewTab === 'deposits' && (
                <>
                  <h3 className="section-subtitle">All Deposits</h3>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>User</th>
                          <th>Reference</th>
                          <th>Provider</th>
                          <th>Requested</th>
                          <th>Credited WKP</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminDeposits.map((entry) => (
                          <tr key={`deposit-${entry.id}`}>
                            <td className="date-cell">{formatDateTime(entry.createdAt || entry.created_at)}</td>
                            <td>{entry.username || entry.userId}</td>
                            <td style={{ maxWidth: 220, wordBreak: 'break-all' }}>{entry.reference}</td>
                            <td>{entry.provider || '-'}</td>
                            <td>{entry.requestedAmount} {entry.currency || ''}</td>
                            <td>{Number(entry.creditedWikiPenny || 0).toLocaleString()}</td>
                            <td><span className={`status-badge ${getPaymentStatusClass(entry.status)}`}>{entry.status}</span></td>
                            <td className="actions-cell">
                              <button className="action-btn view" onClick={() => handleAdminDepositStatusUpdate(entry, 'success')} disabled={adminPaymentActionKey === `deposit-${entry.id}-success`}>Mark Success</button>
                              <button className="action-btn approve" onClick={() => handleAdminDepositStatusUpdate(entry, 'partial_success')} disabled={adminPaymentActionKey === `deposit-${entry.id}-partial_success`}>Mark Partial</button>
                              <button className="action-btn reject" onClick={() => handleAdminDepositStatusUpdate(entry, 'failed')} disabled={adminPaymentActionKey === `deposit-${entry.id}-failed`}>Mark Failed</button>
                            </td>
                          </tr>
                        ))}
                        {!adminDeposits.length && (
                          <tr>
                            <td colSpan="8" className="empty-cell">{adminPaymentsLoading ? 'Loading deposits...' : 'No deposits found'}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="pagination-bar">
                    <button
                      className="pagination-btn"
                      onClick={() => loadAdminDeposits({ page: adminDepositsPage - 1, searchText: paymentSearch, status: depositStatusFilter })}
                      disabled={adminPaymentsLoading || adminDepositsPage <= 1}
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">Page {adminDepositsPage}</span>
                    <button
                      className="pagination-btn"
                      onClick={() => loadAdminDeposits({ page: adminDepositsPage + 1, searchText: paymentSearch, status: depositStatusFilter })}
                      disabled={adminPaymentsLoading || !adminDepositsHasMore}
                    >
                      Next →
                    </button>
                  </div>
                </>
              )}

              {paymentsViewTab === 'withdrawals' && (
                <>
                  <h3 className="section-subtitle" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <span>All Withdrawals</span>
                    <button
                      className="refresh-btn-small"
                      style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        const uid = window.prompt('Enter User ID to deduct/remove WikiPenny from:');
                        if (uid) handleDeductCoinsFromUser(Number(uid));
                      }}
                    >
                      ➖ Remove Coin / Deduct WKP Tool
                    </button>
                  </h3>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>User</th>
                          <th>Reference</th>
                          <th>Amount</th>
                          <th>Network</th>
                          <th>Wallet Address</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminWithdrawals.map((entry) => (
                          <tr key={`withdrawal-${entry.id}`}>
                            <td className="date-cell">{formatDateTime(entry.created_at)}</td>
                            <td>{entry.username || entry.user_id}</td>
                            <td style={{ maxWidth: 220, wordBreak: 'break-all' }}>{entry.reference}</td>
                            <td>
                              {entry.provider === 'bank_transfer' ? (
                                <>
                                  <strong style={{ color: '#10b981' }}>₦{Number(entry.amount).toLocaleString()}</strong>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>{entry.metadata?.debitWikiPenny || '—'} WKP</div>
                                </>
                              ) : (
                                <>{entry.amount} {entry.currency || 'USDT'}</>
                              )}
                            </td>
                            <td>
                              {entry.provider === 'bank_transfer' ? (
                                <span style={{ fontWeight: 'bold' }}>{entry.network}</span>
                              ) : (
                                <>{entry.network || '-'}</>
                              )}
                            </td>
                            <td>
                              {entry.provider === 'bank_transfer' ? (
                                <div>
                                  <div>No: <strong>{entry.wallet_address}</strong></div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                    Name: <strong>{entry.metadata?.accountName || '—'}</strong>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ maxWidth: 320, wordBreak: 'break-all' }}>{entry.wallet_address || '-'}</div>
                              )}
                            </td>
                            <td><span className={`status-badge ${getPaymentStatusClass(entry.status)}`}>{entry.status}</span></td>
                            <td className="actions-cell">
                              <button className="action-btn view" onClick={() => handleAdminWithdrawalStatusUpdate(entry, 'processing')} disabled={adminPaymentActionKey === `withdrawal-${entry.id}-processing`}>Processing</button>
                              <button className="action-btn approve" onClick={() => handleAdminWithdrawalStatusUpdate(entry, 'success')} disabled={adminPaymentActionKey === `withdrawal-${entry.id}-success`}>Paid</button>
                              <button className="action-btn reject" onClick={() => handleAdminWithdrawalStatusUpdate(entry, 'failed')} disabled={adminPaymentActionKey === `withdrawal-${entry.id}-failed`}>Fail + Refund</button>
                              <button
                                className="action-btn reject"
                                style={{ background: '#dc2626', color: '#fff', marginLeft: '4px' }}
                                onClick={() => handleDeductCoinsFromUser(entry.user_id, Math.round(Number(entry.amount || 0) * 100), `withdrawal_manual_deduction_${entry.reference}`)}
                                disabled={loading}
                              >
                                ➖ Deduct WKP
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!adminWithdrawals.length && (
                          <tr>
                            <td colSpan="8" className="empty-cell">{adminPaymentsLoading ? 'Loading withdrawals...' : 'No withdrawals found'}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="pagination-bar">
                    <button
                      className="pagination-btn"
                      onClick={() => loadAdminWithdrawals({ page: adminWithdrawalsPage - 1, searchText: paymentSearch, status: withdrawalStatusFilter })}
                      disabled={adminPaymentsLoading || adminWithdrawalsPage <= 1}
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">Page {adminWithdrawalsPage}</span>
                    <button
                      className="pagination-btn"
                      onClick={() => loadAdminWithdrawals({ page: adminWithdrawalsPage + 1, searchText: paymentSearch, status: withdrawalStatusFilter })}
                      disabled={adminPaymentsLoading || !adminWithdrawalsHasMore}
                    >
                      Next →
                    </button>
                  </div>
                </>
              )}

              {paymentsViewTab === 'bank_transfers' && (
                <>
                  {/* Bank Settings Panel */}
                  <div className="admin-card" style={{ marginTop: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="card-header">
                      <h3 className="card-title">🏦 Bank Account Settings (Naira Transfer)</h3>
                      <p className="card-subtitle">Configure the destination bank account details and conversion rate shown to users</p>
                    </div>
                    {bankSettingsLoading && !bankSettingsData ? (
                      <p>Loading settings...</p>
                    ) : (
                      <form onSubmit={handleSaveBankSettings} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <div className="setting-item" style={{ flex: 1, minWidth: '200px' }}>
                            <label>Bank Name</label>
                            <input
                              type="text"
                              value={bankSettingsData?.bankName || ''}
                              onChange={(e) => setBankSettingsData({ ...bankSettingsData, bankName: e.target.value })}
                              placeholder="e.g. GTBank"
                              required
                            />
                          </div>
                          <div className="setting-item" style={{ flex: 1, minWidth: '200px' }}>
                            <label>Account Number</label>
                            <input
                              type="text"
                              value={bankSettingsData?.bankAccountNumber || ''}
                              onChange={(e) => setBankSettingsData({ ...bankSettingsData, bankAccountNumber: e.target.value })}
                              placeholder="e.g. 0123456789"
                              required
                            />
                          </div>
                          <div className="setting-item" style={{ flex: 1, minWidth: '200px' }}>
                            <label>Account Name</label>
                            <input
                              type="text"
                              value={bankSettingsData?.bankAccountName || ''}
                              onChange={(e) => setBankSettingsData({ ...bankSettingsData, bankAccountName: e.target.value })}
                              placeholder="e.g. WikiGame Ltd"
                              required
                            />
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <div className="setting-item" style={{ flex: 1, minWidth: '200px' }}>
                            <label>Naira to Dollar Conversion Rate (₦ per $1)</label>
                            <input
                              type="number"
                              value={bankSettingsData?.nairaDollarRate || 1500}
                              onChange={(e) => setBankSettingsData({ ...bankSettingsData, nairaDollarRate: Number(e.target.value) })}
                              placeholder="e.g. 1500"
                              min="1"
                              required
                            />
                            <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                              ₦{bankSettingsData?.nairaDollarRate || 1500} = $1 = 100 WKP (WikiPenny)
                            </span>
                          </div>
                          <div className="setting-item" style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!!bankSettingsData?.bankTransferEnabled}
                                onChange={(e) => setBankSettingsData({ ...bankSettingsData, bankTransferEnabled: e.target.checked })}
                                style={{ width: '18px', height: '18px' }}
                              />
                              Enable Bank Transfer Deposits
                            </label>
                          </div>
                        </div>

                        <button type="submit" className="save-btn" disabled={bankSettingsLoading} style={{ alignSelf: 'flex-start', marginTop: '8px' }}>
                          {bankSettingsLoading ? 'Saving...' : 'Save Bank Settings'}
                        </button>
                        {bankSettingsSaved && <p style={{ color: '#10b981', fontSize: '13px', margin: 0 }}>✓ Bank account settings updated successfully!</p>}
                      </form>
                    )}
                  </div>

                  <h3 className="section-subtitle" style={{ marginTop: '24px' }}>Naira Bank Transfer Requests</h3>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>User</th>
                          <th>Reference Code</th>
                          <th>User Description</th>
                          <th>Naira Amount</th>
                          <th>Credited WKP</th>
                          <th>Rate</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankTransferRequests.map((req) => (
                          <tr key={`bank-req-${req.id}`}>
                            <td className="date-cell">{formatDateTime(req.createdAt)}</td>
                            <td>
                              <div>{req.username}</div>
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{req.email}</div>
                            </td>
                            <td style={{ fontFamily: 'monospace', fontWeight: 800, color: '#10b981', fontSize: '14px' }}>{req.reference}</td>
                            <td style={{ maxWidth: '200px', wordBreak: 'break-all', fontSize: '13px', color: '#fbbf24', fontWeight: 500 }}>{req.description || '—'}</td>
                            <td>₦{Number(req.amountNaira).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td>{req.amountWkp.toLocaleString()} WKP</td>
                            <td>₦{req.nairaRate}/$</td>
                            <td>
                              <span className={`status-badge ${req.status === 'approved' ? 'online' : req.status === 'paid' ? 'online' : req.status === 'rejected' ? 'blocked' : ''}`}>
                                {req.status}
                              </span>
                            </td>
                            <td className="actions-cell">
                              {(req.status === 'pending' || req.status === 'paid') ? (
                                <>
                                  <button
                                    className="action-btn approve"
                                    onClick={() => handleApproveBankTransfer(req)}
                                    disabled={bankTransferActionId === req.id}
                                  >
                                    Approve & Credit
                                  </button>
                                  <button
                                    className="action-btn reject"
                                    onClick={() => handleRejectBankTransfer(req)}
                                    disabled={bankTransferActionId === req.id}
                                  >
                                    Reject
                                  </button>
                                </>
                              ) : (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {req.adminUsername ? `Processed by: ${req.adminUsername}` : '—'}
                                  {req.adminNote && <div style={{ fontSize: '11px', fontStyle: 'italic', marginTop: '2px' }}>"{req.adminNote}"</div>}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!bankTransferRequests.length && (
                          <tr>
                            <td colSpan="9" className="empty-cell">{bankTransferLoading ? 'Loading requests...' : 'No bank transfer requests found'}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'messages' && (
          <>
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Broadcast Message</h2>
                <p className="card-subtitle">Send in-app announcements and optional email or SMS to groups or specific members</p>
              </div>
              <form onSubmit={handleCreateAdminMessage}>
                <div className="settings-grid">
                  <div className="setting-item">
                    <label>Title</label>
                    <input
                      type="text"
                      value={messageTitle}
                      onChange={(e) => setMessageTitle(e.target.value)}
                      maxLength={180}
                      placeholder="Message title"
                      required
                    />
                  </div>
                  <div className="setting-item">
                    <label>Audience</label>
                    <select value={messageAudienceType} onChange={(e) => setMessageAudienceType(e.target.value)}>
                      <option value="all">All Members</option>
                      <option value="online">Currently Online</option>
                      <option value="verified">Verified Email Users</option>
                      <option value="unverified">Unverified Email Users</option>
                      <option value="blocked">Blocked Users</option>
                      <option value="selected">Selected User IDs</option>
                    </select>
                  </div>
                  <div className="setting-item">
                    <label>Expiry</label>
                    <input
                      type="datetime-local"
                      value={messageExpiresAt}
                      onChange={(e) => setMessageExpiresAt(e.target.value)}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Selected User IDs</label>
                    <input
                      type="text"
                      value={messageSelectedUserIds}
                      onChange={(e) => setMessageSelectedUserIds(e.target.value)}
                      placeholder="Example: 12, 45, 78"
                      disabled={messageAudienceType !== 'selected'}
                    />
                    <small className="input-hint">Use comma-separated IDs for a group or a single ID for one member.</small>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Body</label>
                  <textarea
                    className="form-input"
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    placeholder="Type the message body here..."
                    rows={6}
                    style={{ resize: 'vertical', minHeight: 140 }}
                    required
                  />
                </div>

                <div className="settings-grid" style={{ marginBottom: 16 }}>
                  <label className="setting-item" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={messageSendInApp} onChange={(e) => setMessageSendInApp(e.target.checked)} />
                    <span>Show in app dashboard</span>
                  </label>
                  <label className="setting-item" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={messageSendEmail} onChange={(e) => setMessageSendEmail(e.target.checked)} />
                    <span>Send email</span>
                  </label>
                  <label className="setting-item" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={messageSendSms} onChange={(e) => setMessageSendSms(e.target.checked)} />
                    <span>Send SMS</span>
                  </label>
                  <label className="setting-item" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={messageDismissible} onChange={(e) => setMessageDismissible(e.target.checked)} />
                    <span>Allow users to dismiss</span>
                  </label>
                </div>

                {messageSendEmail ? (
                  <div className="admin-card" style={{ marginBottom: 16, padding: 16, border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="card-header" style={{ marginBottom: 12 }}>
                      <h3 className="card-title" style={{ marginBottom: 4 }}>Email Template</h3>
                      <p className="card-subtitle">Choose a styled template or switch to custom HTML for full control.</p>
                    </div>
                    <div className="settings-grid">
                      <div className="setting-item">
                        <label>Email Provider</label>
                        <select value={messageEmailProvider} onChange={(e) => setMessageEmailProvider(e.target.value)}>
                          <option value="default">Default (Resend fallback to Gmail)</option>
                          <option value="resend">Resend</option>
                          <option value="gmail">Gmail</option>
                        </select>
                      </div>
                      <div className="setting-item">
                        <label>Email Mode</label>
                        <select value={messageEmailHtmlMode} onChange={(e) => setMessageEmailHtmlMode(e.target.value)}>
                          <option value="template">Styled template</option>
                          <option value="custom">Custom HTML</option>
                        </select>
                      </div>
                      <div className="setting-item">
                        <label>Template Style</label>
                        <select
                          value={messageEmailTemplate}
                          onChange={(e) => setMessageEmailTemplate(e.target.value)}
                          disabled={messageEmailHtmlMode === 'custom'}
                        >
                          <option value="announcement">Announcement</option>
                          <option value="promo">Promo</option>
                          <option value="alert">Alert</option>
                        </select>
                      </div>
                      <div className="setting-item">
                        <label>CTA Label</label>
                        <input
                          type="text"
                          value={messageEmailCtaLabel}
                          onChange={(e) => setMessageEmailCtaLabel(e.target.value)}
                          placeholder="Optional button text"
                          disabled={messageEmailHtmlMode === 'custom'}
                        />
                      </div>
                      <div className="setting-item">
                        <label>CTA URL</label>
                        <input
                          type="url"
                          value={messageEmailCtaUrl}
                          onChange={(e) => setMessageEmailCtaUrl(e.target.value)}
                          placeholder="https://wikigame.top/..."
                          disabled={messageEmailHtmlMode === 'custom'}
                        />
                      </div>
                    </div>

                    {messageEmailHtmlMode === 'custom' ? (
                      <div className="form-group" style={{ marginTop: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Custom HTML</label>
                        <textarea
                          className="form-input"
                          value={messageEmailCustomHtml}
                          onChange={(e) => setMessageEmailCustomHtml(e.target.value)}
                          placeholder="<html>...your custom email template...</html>"
                          rows={10}
                          style={{ resize: 'vertical', minHeight: 220, fontFamily: 'monospace' }}
                          required={messageSendEmail && messageEmailHtmlMode === 'custom'}
                        />
                        <small className="input-hint">Custom HTML is only used for email. The normal body still appears in-app and in admin history.</small>
                      </div>
                    ) : (
                      <small className="input-hint">The normal message body will be wrapped in the selected HTML email template.</small>
                    )}
                  </div>
                ) : null}

                <div className="admin-actions">
                  <button type="submit" className="save-btn" disabled={loading}>
                    Send Message
                  </button>
                </div>
              </form>
            </div>

            <div className="admin-card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="card-title">Recent Messages</h2>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {adminMessages.length > 0 && (
                    <button
                      className="refresh-btn-small"
                      style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                      onClick={handleDeleteAllAdminMessages}
                      disabled={loading}
                    >
                      🗑️ Delete All Messages
                    </button>
                  )}
                  <button className="refresh-btn-small" onClick={loadAdminMessages} disabled={loading}>
                    ⟳ Refresh
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Title</th>
                      <th>Audience</th>
                      <th>Channels</th>
                      <th>Recipients</th>
                      <th>Delivery</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminMessages.map((message) => (
                      <tr key={message.id}>
                        <td className="date-cell">{formatDateTime(message.created_at)}</td>
                        <td>
                          <div style={{ fontWeight: 700 }}>{message.title}</div>
                          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4, whiteSpace: 'pre-wrap' }}>{message.body}</div>
                          {message.send_email ? (
                            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
                              Email mode: {message.email_html_mode === 'custom' ? 'custom HTML' : (message.email_template || 'announcement')}
                              {message.email_provider && ` | Provider: ${message.email_provider}`}
                            </div>
                          ) : null}
                        </td>
                        <td>{String(message.audience_type || 'all').replace('_', ' ')}</td>
                        <td>
                          {[
                            message.send_in_app ? 'In-app' : null,
                            message.send_email ? 'Email' : null,
                            message.send_sms ? 'SMS' : null,
                          ].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td>{Number(message.recipient_count || 0)}</td>
                        <td>
                          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                            <div>Email: {Number(message.email_sent_count || 0)} sent / {Number(message.email_failed_count || 0)} failed</div>
                            <div>SMS: {Number(message.sms_sent_count || 0)} sent / {Number(message.sms_failed_count || 0)} failed</div>
                            {message.delivery_note ? (
                              <div style={{ opacity: 0.75, marginTop: 4 }}>{message.delivery_note}</div>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <button
                            style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => handleDeleteAdminMessage(message.id)}
                            disabled={loading}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {adminMessages.length === 0 && (
                      <tr>
                        <td colSpan="7" className="empty-cell">No admin messages yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            {/* Balance Adjustment */}
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Adjust User Balance (Credit / Deduct WKP)</h2>
                <p className="card-subtitle">Credit or deduct WikiPenny directly from any player's wallet</p>
              </div>
              <form className="balance-form" onSubmit={handleBalanceAdjust}>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      style={{
                        flex: 1, padding: '8px', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
                        background: balanceMode === 'add' ? '#10b981' : 'rgba(255,255,255,0.05)',
                        color: '#fff', border: `1px solid ${balanceMode === 'add' ? '#10b981' : 'rgba(255,255,255,0.15)'}`
                      }}
                      onClick={() => setBalanceMode('add')}
                    >
                      ➕ Credit / Add WKP
                    </button>
                    <button
                      type="button"
                      style={{
                        flex: 1, padding: '8px', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
                        background: balanceMode === 'deduct' ? '#ef4444' : 'rgba(255,255,255,0.05)',
                        color: '#fff', border: `1px solid ${balanceMode === 'deduct' ? '#ef4444' : 'rgba(255,255,255,0.15)'}`
                      }}
                      onClick={() => setBalanceMode('deduct')}
                    >
                      ➖ Deduct / Remove WKP
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Target User ID"
                    value={balanceTargetUserId}
                    onChange={(e) => setBalanceTargetUserId(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    className="form-input"
                    placeholder={balanceMode === 'deduct' ? "Amount of WKP to deduct (e.g. 500)" : "Amount of WKP to credit (e.g. 500)"}
                    value={balanceDelta}
                    onChange={(e) => setBalanceDelta(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Reason for adjustment (optional)"
                    value={balanceReason}
                    onChange={(e) => setBalanceReason(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="save-btn"
                  style={{ background: balanceMode === 'deduct' ? '#ef4444' : '#10b981' }}
                  disabled={loading}
                >
                  {balanceMode === 'deduct' ? '➖ Deduct WKP from Wallet' : '➕ Credit WKP to Wallet'}
                </button>
              </form>
            </div>

            {/* Platform Settings */}
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Platform Settings</h2>
                <p className="card-subtitle">Configure global platform parameters</p>
              </div>

              <div className="settings-grid">
                <div className="setting-item">
                  <label>Platform Charge (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings?.chargePercent ?? 7}
                    onChange={(e) => setSettings((prev) => ({ ...prev, chargePercent: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Minimum Wager</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings?.minimumWager ?? 50}
                    onChange={(e) => setSettings((prev) => ({ ...prev, minimumWager: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Minimum Deposit</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings?.minimumDeposit ?? 2}
                    onChange={(e) => setSettings((prev) => ({ ...prev, minimumDeposit: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Minimum Withdrawal (WKP)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings?.minimumWithdrawal ?? 200}
                    onChange={(e) => setSettings((prev) => ({ ...prev, minimumWithdrawal: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Signup Credits</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings?.signupCredit ?? 50}
                    onChange={(e) => setSettings((prev) => ({ ...prev, signupCredit: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Referral Bonus (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings?.referralBonusPercent ?? 10}
                    onChange={(e) => setSettings((prev) => ({ ...prev, referralBonusPercent: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>Online Members Source</label>
                  <select
                    value={settings?.onlineMembersMode || 'manual'}
                    onChange={(e) => setSettings((prev) => ({ ...prev, onlineMembersMode: e.target.value }))}
                  >
                    <option value="manual">Manual</option>
                    <option value="live">Live Online Users</option>
                    <option value="random">Random Range</option>
                  </select>
                  <small className="input-hint">
                    {settings?.onlineMembersMode === 'live'
                      ? `Current live count: ${Number(stats?.currentlyOnline || 0).toLocaleString()}`
                      : settings?.onlineMembersMode === 'random'
                        ? 'Show a random online-members value from a configured range.'
                        : 'Use a fixed number for the dashboard badge.'}
                  </small>
                </div>
                <div className="setting-item">
                  <label>Online Members Display</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings?.onlineMembers ?? 2847}
                    onChange={(e) => setSettings((prev) => ({ ...prev, onlineMembers: e.target.value }))}
                    disabled={settings?.onlineMembersMode !== 'manual'}
                  />
                </div>
                <div className="setting-item">
                  <label>Random Online Min</label>
                  <input
                    type="number"
                    min="20"
                    max="200"
                    step="1"
                    value={settings?.onlineMembersRandomMin ?? 20}
                    onChange={(e) => setSettings((prev) => ({ ...prev, onlineMembersRandomMin: e.target.value }))}
                    disabled={settings?.onlineMembersMode !== 'random'}
                  />
                </div>
                <div className="setting-item">
                  <label>Random Online Max</label>
                  <input
                    type="number"
                    min="20"
                    max="200"
                    step="1"
                    value={settings?.onlineMembersRandomMax ?? 200}
                    onChange={(e) => setSettings((prev) => ({ ...prev, onlineMembersRandomMax: e.target.value }))}
                    disabled={settings?.onlineMembersMode !== 'random'}
                  />
                </div>
                <div className="setting-item">
                  <label>Random Refresh (sec)</label>
                  <input
                    type="number"
                    min="5"
                    max="3600"
                    step="1"
                    value={settings?.onlineMembersRandomIntervalSeconds ?? 30}
                    onChange={(e) => setSettings((prev) => ({ ...prev, onlineMembersRandomIntervalSeconds: e.target.value }))}
                    disabled={settings?.onlineMembersMode !== 'random'}
                  />
                </div>
              </div>

              {/* Prize Distribution */}
              <div className="distribution-section">
                <h3 className="section-subtitle">Prize Distribution (%)</h3>
                {[2, 3, 4, 5].map((playerCount) => {
                  const distribution = settings?.prizeDistribution?.[playerCount] || [];
                  const total = getDistributionTotal(playerCount);
                  const isValid = Math.abs(total - 100) < 0.001;
                  return (
                    <div key={playerCount} className="distribution-block">
                      <div className="distribution-header">
                        <h4>{playerCount} Players</h4>
                        <span className={`distribution-total ${isValid ? 'valid' : 'invalid'}`}>
                          Total: {total.toFixed(2)}%
                        </span>
                      </div>
                      <div className="distribution-inputs">
                        {Array.from({ length: playerCount }).map((_, index) => (
                          <div key={`${playerCount}-${index}`} className="distribution-input">
                            <label>Rank {index + 1}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={distribution[index] ?? 0}
                              onChange={(e) => updateDistributionCell(playerCount, index, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="admin-actions">
                <button className="save-btn" onClick={saveSettings} disabled={loading || !isDistributionValid}>
                  💾 Save Settings
                </button>
                {!isDistributionValid && (
                  <span className="error-hint">⚠️ Each distribution must total exactly 100%</span>
                )}
              </div>
            </div>

            {/* Withdrawal Controls */}
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Withdrawal Controls</h2>
                <p className="card-subtitle">Manage payout mode and pending withdrawals</p>
              </div>

              <div className="mode-controls">
                <div className="mode-info">
                  <span className="mode-label">Current Mode:</span>
                  <span className={`mode-value ${settings?.withdrawalMode || 'admin_approve'}`}>
                    {settings?.withdrawalMode === 'auto' ? '⚡ Auto-Approval' : '🔒 Admin Approval Required'}
                  </span>
                </div>
                <div className="mode-actions">
                  <button
                    className={`mode-btn ${settings?.withdrawalMode === 'admin_approve' ? 'active' : ''}`}
                    onClick={() => updateWithdrawalModeOnly('admin_approve')}
                    disabled={loading || settings?.withdrawalMode === 'admin_approve'}
                  >
                    Admin Approve
                  </button>
                  <button
                    className={`mode-btn ${settings?.withdrawalMode === 'auto' ? 'active' : ''}`}
                    onClick={() => updateWithdrawalModeOnly('auto')}
                    disabled={loading || settings?.withdrawalMode === 'auto'}
                  >
                    Auto
                  </button>
                  <button className="refresh-btn-small" onClick={loadPendingWithdrawals} disabled={pendingWithdrawalsLoading}>
                    ⟳ Refresh
                  </button>
                </div>
              </div>

              {pendingWithdrawals.length > 0 && (
                <div className="pending-table">
                  <h3>Pending Withdrawals ({pendingWithdrawals.length})</h3>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>User</th>
                          <th>Amount</th>
                          <th>Network</th>
                          <th>Address</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingWithdrawals.map((entry) => (
                          <tr key={entry.id}>
                            <td className="date-cell">{formatDateTime(entry.created_at)}</td>
                            <td>{entry.username || entry.user_id}</td>
                            <td className="amount-cell">{entry.amount} {entry.currency || 'USDT'}</td>
                            <td>{entry.network || '—'}</td>
                            <td className="address-cell" style={{ maxWidth: 320, wordBreak: 'break-all' }}>{entry.wallet_address || '-'}</td>
                            <td><span className={`status-badge ${getPaymentStatusClass(entry.status || 'pending')}`}>{entry.status || 'pending'}</span></td>
                            <td className="actions-cell">
                              <button
                                className="action-btn approve"
                                onClick={() => approvePendingWithdrawal(entry)}
                                disabled={withdrawActionId === entry.id}
                              >
                                Approve
                              </button>
                              <button
                                className="action-btn reject"
                                onClick={() => rejectPendingWithdrawal(entry.id)}
                                disabled={withdrawActionId === entry.id}
                              >
                                Reject
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {pendingWithdrawals.length === 0 && !pendingWithdrawalsLoading && (
                <div className="empty-state-small">
                  <span className="empty-icon">💸</span>
                  <span>No pending withdrawals</span>
                </div>
              )}
            </div>

            {/* Escrow Recovery */}
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Escrow Recovery</h2>
                <p className="card-subtitle">Fix hanging deducted wagers for stale game records</p>
              </div>

              <div className="reconcile-controls">
                <div className="reconcile-option">
                  <label>Game Scope</label>
                  <select value={reconcileGame} onChange={(e) => setReconcileGame(e.target.value)}>
                    <option value="all">All Games</option>
                    <option value="reaction">Reaction</option>
                    <option value="flappy">Flappy</option>
                    <option value="chess">Chess</option>
                    <option value="checkers">Checkers</option>
                    <option value="draughts">Draughts</option>
                    <option value="whot">WHOT</option>
                  </select>
                </div>
                <div className="reconcile-option">
                  <label>Older than (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={reconcileMinutes}
                    onChange={(e) => setReconcileMinutes(e.target.value)}
                  />
                </div>
                <div className="reconcile-option">
                  <label>Max rows per run</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={reconcileLimit}
                    onChange={(e) => setReconcileLimit(e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-actions">
                <button className="dryrun-btn" onClick={() => runEscrowReconcile({ execute: false })} disabled={loading}>
                  🔍 Dry Run
                </button>
                <button className="recover-btn" onClick={() => runEscrowReconcile({ execute: true })} disabled={loading}>
                  🔧 Fix Hanging Escrow
                </button>
              </div>

              {reconcileSummary && (
                <div className="reconcile-summary">
                  <div className="summary-item">
                    <span className="summary-label">Mode:</span>
                    <span className="summary-value">{reconcileSummary.mode || (reconcileSummary.dryRun ? 'dry-run' : 'execute')}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Candidates:</span>
                    <span className="summary-value">{reconcileSummary.candidateCount ?? 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Refund:</span>
                    <span className="summary-value">{reconcileSummary?.summary?.totalRefund ?? 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Applied Refunds:</span>
                    <span className="summary-value">{reconcileSummary?.appliedCount ?? reconcileSummary?.summary?.refunded ?? 0}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Two-Factor Authentication */}
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Two-Factor Authentication (2FA)</h2>
                <p className="card-subtitle">Protect this admin account with a TOTP authenticator app</p>
              </div>

              {totpError && <div className="error-message" style={{ marginBottom: 12 }}>{totpError}</div>}
              {totpMessage && <div className="success-message" style={{ marginBottom: 12 }}>{totpMessage}</div>}

              {totpEnabled === null && <p style={{ color: '#aaa' }}>Loading 2FA status…</p>}

              {totpEnabled === true && !totpSetupData && (
                <div>
                  <p style={{ color: '#4caf50', fontWeight: 600, marginBottom: 8 }}>✔ 2FA is enabled on this account.</p>
                  <p style={{ marginBottom: 8 }}>To disable it, enter your current TOTP code:</p>
                  <form onSubmit={handleTotpDisable} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="text" inputMode="numeric" placeholder="000 000"
                      value={totpDisableCode} onChange={(e) => setTotpDisableCode(e.target.value)}
                      maxLength={7} style={{ width: 110, letterSpacing: '0.15em', textAlign: 'center' }} required />
                    <button type="submit" className="danger-btn"
                      disabled={totpLoading || totpDisableCode.replace(/\s/g, '').length < 6}>
                      {totpLoading ? 'Disabling…' : 'Disable 2FA'}
                    </button>
                  </form>
                </div>
              )}

              {totpEnabled === false && !totpSetupData && (
                <div>
                  <p style={{ color: '#e67e22', marginBottom: 12 }}>2FA is not enabled. Enable it to protect against stolen passwords.</p>
                  <button className="action-btn" onClick={handleTotpSetupStart} disabled={totpLoading}>
                    {totpLoading ? 'Generating…' : 'Set up 2FA'}
                  </button>
                </div>
              )}

              {totpSetupData && (
                <div>
                  <p style={{ marginBottom: 8 }}>Scan this QR code with Google Authenticator or Authy:</p>
                  <img src={totpSetupData.qrDataUrl} alt="TOTP QR code"
                    style={{ display: 'block', width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 4, marginBottom: 10 }} />
                  <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 12 }}>
                    Can't scan? Enter this secret manually: <code style={{ userSelect: 'all' }}>{totpSetupData.secret}</code>
                  </p>
                  <p style={{ marginBottom: 8 }}>Enter the 6-digit code your app shows to activate 2FA:</p>
                  <form onSubmit={handleTotpVerifySetup} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="text" inputMode="numeric" placeholder="000 000"
                      value={totpSetupCode} onChange={(e) => setTotpSetupCode(e.target.value)}
                      maxLength={7} style={{ width: 110, letterSpacing: '0.15em', textAlign: 'center' }} autoFocus required />
                    <button type="submit" className="action-btn"
                      disabled={totpLoading || totpSetupCode.replace(/\s/g, '').length < 6}>
                      {totpLoading ? 'Activating…' : 'Activate 2FA'}
                    </button>
                    <button type="button" className="secondary-btn"
                      onClick={() => { setTotpSetupData(null); setTotpSetupCode(''); setTotpError(''); }}>
                      Cancel
                    </button>
                  </form>
                </div>
              )}
            </div>
          </>
        )}

        {/* User Profile Modal */}
        {profileOpen && selectedUser && (
          <div className="modal-overlay" onClick={() => setProfileOpen(false)}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="modal-icon">👤</div>
                  <h3 className="modal-title">Member Profile</h3>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto', marginRight: 12 }}>
                  <button
                    className="action-btn approve"
                    style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => handleAddCoinsToUser(selectedUser.id)}
                    disabled={loading}
                  >
                    ➕ Add WKP
                  </button>
                  <button
                    className="action-btn reject"
                    style={{ background: '#ef4444', color: '#fff', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => handleDeductCoinsFromUser(selectedUser.id)}
                    disabled={loading}
                  >
                    ➖ Deduct WKP
                  </button>
                </div>
                <button className="modal-close" onClick={() => setProfileOpen(false)}>×</button>
              </div>
              <div className="profile-grid">
                <div className="profile-field">
                  <span className="field-label">Full Name</span>
                  <span className="field-value">{selectedUser.full_name || '—'}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">ID</span>
                  <span className="field-value">{selectedUser.id}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Username</span>
                  <span className="field-value">{selectedUser.username}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Email</span>
                  <span className="field-value">{selectedUser.email}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Reg. IP</span>
                  <span className="field-value">{selectedUser.registration_ip || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Login IP</span>
                  <span className="field-value">{selectedUser.last_login_ip || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Phone</span>
                  <span className="field-value">{selectedUser.phone || '—'}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Role</span>
                  <span className={`role-badge ${selectedUser.role}`}>{selectedUser.role}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Balance</span>
                  <span className="field-value balance">{selectedUser.balance}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Status</span>
                  <span className={`status-badge ${selectedUser.is_blocked ? 'blocked' : 'active'}`}>
                    {selectedUser.is_blocked ? 'Blocked' : 'Active'}
                  </span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Email Verification</span>
                  <span className={`status-badge ${selectedUser.is_email_verified ? 'online' : 'offline'}`}>
                    {selectedUser.is_email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Last Login</span>
                  <span className="field-value">{formatDateTime(selectedUser.last_login_at)}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Joined</span>
                  <span className="field-value">{formatDateTime(selectedUser.created_at)}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Verified At</span>
                  <span className="field-value">{formatDateTime(selectedUser.email_verified_at)}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Referral Code</span>
                  <span className="field-value">{selectedUser.referral_code || '—'}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Referred By</span>
                  <span className="field-value">
                    {selectedUser.referred_by_username
                      ? `${selectedUser.referred_by_username} (#${selectedUser.referred_by_user_id})`
                      : '—'}
                  </span>
                </div>
                <div className="profile-field">
                  <span className="field-label">People Referred</span>
                  <span className="field-value">{Number(selectedUser.referral_count || 0)}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Referral Earnings</span>
                  <span className="field-value balance">{Number(selectedUser.earned_from_referrals || 0).toFixed(2)} WKP</span>
                </div>
              </div>

              {!selectedUser.is_email_verified && (
                <div className="admin-actions" style={{ padding: '0 20px 12px' }}>
                  <button
                    className="save-btn"
                    onClick={() => verifyEmailManual(selectedUser)}
                    disabled={loading}
                  >
                    Verify User Email Manually
                  </button>
                </div>
              )}

              <h4 className="transactions-title">Users Referred By This Member</h4>
              <div className="transactions-list">
                {Array.isArray(selectedUser.referred_users) && selectedUser.referred_users.length > 0 ? (
                  selectedUser.referred_users.map((person) => (
                    <div key={person.id} className="transaction-item">
                      <span className="transaction-date">{formatDateTime(person.created_at)}</span>
                      <span className="field-value">{person.full_name || person.username}</span>
                      <span className="transaction-category">@{person.username}</span>
                      <span className={`status-badge ${person.is_email_verified ? 'online' : 'offline'}`}>
                        {person.is_email_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="empty-state-small">No referred users yet</div>
                )}
              </div>

              {/* Individual Player Transaction Ledger */}
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                  <h4 className="transactions-title" style={{ margin: 0 }}>
                    💳 Full Transaction History ({userTxData.total})
                  </h4>
                  {userTxData.transactions.length > 0 && (
                    <button
                      className="refresh-btn-small"
                      style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => exportUserTransactionsCSV(selectedUser, userTxData.transactions)}
                    >
                      📥 Export Player CSV
                    </button>
                  )}
                </div>

                {/* Category Breakdown Chips */}
                {userTxData.categorySummary.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                    <div
                      onClick={() => handleUserTxCategoryChange('all')}
                      style={{
                        padding: '6px 12px',
                        background: userTxCategory === 'all' ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${userTxCategory === 'all' ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: 10,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      <span style={{ fontWeight: 800 }}>All Categories</span>
                      <span style={{ opacity: 0.7, marginLeft: 6 }}>({userTxData.total})</span>
                    </div>
                    {userTxData.categorySummary.map((sum) => {
                      const net = Number(sum.total_credits || 0) - Number(sum.total_debits || 0);
                      return (
                        <div
                          key={sum.category}
                          onClick={() => handleUserTxCategoryChange(sum.category)}
                          style={{
                            padding: '6px 12px',
                            background: userTxCategory === sum.category ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${userTxCategory === sum.category ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                            borderRadius: 10,
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                        >
                          <span style={{ fontWeight: 800, textTransform: 'capitalize' }}>{sum.category.replace('_', ' ')}</span>
                          <span style={{ opacity: 0.7, marginLeft: 6 }}>({sum.count})</span>
                          <span style={{ marginLeft: 6, fontWeight: 800, color: net >= 0 ? '#10b981' : '#ef4444' }}>
                            {net >= 0 ? `+${net}` : net} WKP
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Filter and Search Bar */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <select
                    className="filter-select"
                    style={{ flex: 1, minWidth: 140, padding: '8px 12px', borderRadius: 8, background: 'rgba(15,23,42,0.8)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                    value={userTxCategory}
                    onChange={(e) => handleUserTxCategoryChange(e.target.value)}
                  >
                    <option value="all">All Categories ({userTxData.total})</option>
                    {userTxData.categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>

                  <form onSubmit={handleUserTxSearchSubmit} style={{ display: 'flex', flex: 2, minWidth: 200, gap: 6 }}>
                    <input
                      type="text"
                      className="search-input"
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, background: 'rgba(15,23,42,0.8)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                      placeholder="Search description, reference, game..."
                      value={userTxSearch}
                      onChange={(e) => setUserTxSearch(e.target.value)}
                    />
                    <button type="submit" className="save-btn" style={{ padding: '8px 16px' }}>
                      Search
                    </button>
                  </form>
                </div>

                {/* Individual Transactions List Table */}
                <div className="table-responsive">
                  <table className="admin-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Date & Time</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Balance After</th>
                        <th>Description / Game / Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userTxData.transactions.map((entry) => {
                        const isCredit = Number(entry.delta) > 0;

                        return (
                          <tr key={entry.id}>
                            <td className="date-cell" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.created_at)}</td>
                            <td>
                              <span
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  textTransform: 'uppercase',
                                  background: entry.category === 'wager' ? 'rgba(239,68,68,0.2)'
                                    : entry.category === 'payout' ? 'rgba(16,185,129,0.2)'
                                    : entry.category === 'deposit' ? 'rgba(59,130,246,0.2)'
                                    : entry.category === 'withdrawal' ? 'rgba(245,158,11,0.2)'
                                    : entry.category.includes('bonus') || entry.category === 'achievement' ? 'rgba(168,85,247,0.2)'
                                    : 'rgba(100,116,139,0.2)',
                                  color: entry.category === 'wager' ? '#f87171'
                                    : entry.category === 'payout' ? '#34d399'
                                    : entry.category === 'deposit' ? '#60a5fa'
                                    : entry.category === 'withdrawal' ? '#fbbf24'
                                    : entry.category.includes('bonus') || entry.category === 'achievement' ? '#c084fc'
                                    : '#94a3b8'
                                }}
                              >
                                {entry.category.replace('_', ' ')}
                              </span>
                            </td>
                            <td style={{ fontWeight: 800, color: isCredit ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>
                              {isCredit ? '+' : ''}{entry.delta} WKP
                            </td>
                            <td style={{ fontWeight: 700, opacity: 0.9, whiteSpace: 'nowrap' }}>
                              {entry.balance_after} WKP
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{entry.description || entry.category}</div>
                              {(entry.game || entry.reference_type || entry.reference_id) && (
                                <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>
                                  {entry.game && <span style={{ marginRight: 8 }}>🎮 {entry.game}</span>}
                                  {entry.reference_type && <span>Ref: {entry.reference_type} #{entry.reference_id}</span>}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {userTxData.transactions.length === 0 && (
                        <tr>
                          <td colSpan="5" className="empty-cell">
                            {userTxLoading ? 'Loading player transactions...' : 'No transactions found for this filter'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {userTxData.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                    <button
                      className="refresh-btn-small"
                      disabled={userTxPage <= 1 || userTxLoading}
                      onClick={() => handleUserTxPageChange(userTxPage - 1)}
                    >
                      ◀ Previous
                    </button>
                    <span style={{ fontSize: 13, opacity: 0.8 }}>
                      Page {userTxPage} of {userTxData.totalPages}
                    </span>
                    <button
                      className="refresh-btn-small"
                      disabled={userTxPage >= userTxData.totalPages || userTxLoading}
                      onClick={() => handleUserTxPageChange(userTxPage + 1)}
                    >
                      Next ▶
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
